import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Checkbox } from "@mui/material";
import clsx from "clsx";
import React, { type JSX, useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { currentTheme } from "../../../../atoms";
import checkboxBulletIcon from "../../../../static/images/icons/checkbox-bullet.png";
import checkboxDownArrowIcon from "../../../../static/images/icons/checkbox-down-arrow.png";
import checkboxHorizontalIcon from "../../../../static/images/icons/checkbox-horizontal.png";
import checkboxLeftArrowIcon from "../../../../static/images/icons/checkbox-left-arrow.png";
import checkboxRejectIcon from "../../../../static/images/icons/checkbox-reject.png";
import checkboxRequiredIcon from "../../../../static/images/icons/checkbox-required.png";
import checkboxRightArrowBlueIcon from "../../../../static/images/icons/checkbox-right-arrow-blue.png";
import checkboxRightArrowIcon from "../../../../static/images/icons/checkbox-right-arrow.png";
import checkboxRightArrowRedIcon from "../../../../static/images/icons/checkbox-right-arrow-red.png";
import checkboxUnknownIcon from "../../../../static/images/icons/checkbox-unknown.png";
import checkboxUpArrowIcon from "../../../../static/images/icons/checkbox-up-arrow.png";
import checkboxVerticalIcon from "../../../../static/images/icons/checkbox-vertical.png";

interface CheckBoxProps {
  CheckState?: string | number;
  customDefaultIcons?: Record<number, JSX.Element>;
  MultiCheckSet?: number;
  disabled?: boolean;
  display?: string;
  parentProps?: { name?: string; id?: string | number };
  canModify?: () => boolean;
  onEdit?: (data: CheckEditData) => void | Promise<void>;
}

export interface CheckEditData {
  CheckState: string;
  record?: any;
  fieldname?: string;
  groupid?: string;
  current?: string;
  textchanged?: "0";
  setStateCheckBox?: React.Dispatch<React.SetStateAction<number>>;
  name?: string;
  id?: string;
}

// функция получения массива целочисленных состаяний
function getCheckstate(multicheckSet?: number): number[] {
  // по умолчанию состояния с галочкой и без
  if (!multicheckSet) return [0, 1];
  const result = [];
  let checkState = multicheckSet;
  let value;
  for (let i = 0; i < 14; i++) {
    if (checkState === 0) break;
    value = Math.pow(2, i);
    if (checkState & value) {
      checkState -= value;
      result.push(i);
    }
  }
  return result;
}
// функция получения следующего состояния из массива состояний
// аргументы : 1. массив с целочисленными состояниям, 2. текущее целочисленное состояние
function getNextState(arr: number[], state: string | number) {
  const length = arr.length;
  const index = arr.indexOf(Number(state));

  return index < length - 1 ? arr[index + 1] : arr[0];
}

export default function CheckBoxMultiCheck(props: CheckBoxProps) {
  const {
    CheckState: checkState,
    customDefaultIcons,
    MultiCheckSet: multiCheckSet,
    disabled,
    display,
    parentProps,
    canModify,
    onEdit,
  } = props;
  const theme = useAtomValue(currentTheme);

  // Иконки состояний
  const Unchecked = (
    <CheckBoxOutlineBlankIcon checkicon={"true"} className={clsx("fontColorStimate", { light: theme === "light" })} />
  );
  const Checked = (
    <CheckBoxIcon checkicon={"true"} className={clsx("fontColorStimate", { light: theme === "light" })} />
  );
  const LeftArrow = <img checkicon={"true"} src={checkboxLeftArrowIcon} />;
  const UpArrow = <img checkicon={"true"} src={checkboxUpArrowIcon} />;
  const Horizontal = <img checkicon={"true"} src={checkboxHorizontalIcon} />;
  const Vertical = <img checkicon={"true"} src={checkboxVerticalIcon} />;
  const RightArrow = <img checkicon={"true"} src={checkboxRightArrowIcon} />;
  const DownArrow = <img checkicon={"true"} src={checkboxDownArrowIcon} />;
  const Bullet = <img checkicon={"true"} src={checkboxBulletIcon} />;
  const Required = <img checkicon={"true"} src={checkboxRequiredIcon} />;
  const LeftArrowBlue = (
    <img style={{ transform: "rotate(180deg)" }} checkicon={"true"} src={checkboxRightArrowBlueIcon} />
  );
  const RightArrowRed = <img checkicon={"true"} src={checkboxRightArrowRedIcon} />;
  const Unknown = <img checkicon={"true"} src={checkboxUnknownIcon} />;
  const Reject = <img checkicon={"true"} src={checkboxRejectIcon} />;

  // функция получения иконки по целочисленному состоянию
  function getIcon(checkstate: number, customIcons?: Record<number, JSX.Element>) {
    const icons: Record<number, JSX.Element> = {
      0: Unchecked,
      1: Checked,
      2: LeftArrow,
      3: UpArrow,
      4: Horizontal,
      5: Vertical,
      6: RightArrow,
      7: DownArrow,
      8: Bullet,
      9: Required,
      10: LeftArrowBlue,
      11: RightArrowRed,
      12: Unknown,
      13: Reject,
    };

    return (customIcons && customIcons[checkstate]) || icons[checkstate];
  }

  // Иконка чекбокса
  const [icon, setIcon] = useState(getIcon(checkState ? Number(checkState) : 0, customDefaultIcons));
  // захардкоженое состояние чекбокса блокирующее состояние атрибута checked
  const [checked, setChecked] = useState(false);
  // текущее состояние чекбокса
  const [stateCheckBox, setStateCheckBox] = useState<number>(0);
  // массив состояний, в котором хранятся допустимые состояния
  const checkStates = useRef<number[]>([0, 1]);
  const checkboxRef = useRef<any>(null);

  useEffect(() => {
    if (!checkboxRef.current) return;
    checkboxRef.current.update = (data: { CheckState: string | number; MultiCheckSet?: number }) => {
      checkStates.current = getCheckstate(data.MultiCheckSet);
      setStateCheckBox(data.CheckState ? Number(data.CheckState) : 0);
    };
  }, []);

  useEffect(() => {
    setChecked(false);
    // установка массива состояний
    checkStates.current = getCheckstate(multiCheckSet);
    // получение текущего состояния
    setStateCheckBox(checkState ? Number(checkState) : 0);
  }, [checkState]);

  useEffect(() => {
    // при изменении текущего состояния обновляется иконка чекбокса
    if (stateCheckBox !== undefined) {
      setIcon(getIcon(stateCheckBox, customDefaultIcons));
    }
  }, [stateCheckBox]);

  return (
    <div
      id="checkbox-multicheck"
      ref={checkboxRef}
      style={{ width: "100%", height: "100%", alignItems: "center", display: "flex" }}
    >
      <Checkbox
        // иконка чекбокса
        disabled={disabled}
        icon={icon}
        checkedIcon={icon}
        checked={checked}
        onChange={(ev) => {
          ev.stopPropagation();
          // получение следуйщего состояния
          if (canModify?.() === false) {
            return;
          }
          let state = getNextState(checkStates.current, stateCheckBox);
          setStateCheckBox(state);

          const record: any = ev.target.closest("TR");
          if (!onEdit) return;
          if (record) {
            record.dataset.checkstate = state;
            // функция на редактировние обновит состояние чекбокса из ответа внутри функции
            onEdit({
              record: record,
              fieldname: record.dataset.fieldname,
              groupid: record.dataset.groupid,
              current: record.dataset.current,
              CheckState: `${state}`,
              textchanged: "0",
              setStateCheckBox: setStateCheckBox,
            });
          } else {
            onEdit({
              name: parentProps?.name || undefined,
              id: parentProps?.id || undefined,
              CheckState: `${state}`,
            });
          }
        }}
        style={{
          width: "21px",
          height: "21px",
          display: display || "",
        }}
        sx={{
          "& .MuiSvgIcon-root": { fontSize: 18 },
        }}
      />
    </div>
  );
}
